import { useTheme } from '@shopify/restyle';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { Box, Text } from '@components/Restyle';
import Icon from '@components/shared/Icon/Icon';
import ToggleSwitch from '@components/Tasks/ToggleSwitch.web';
import { BillingCycle } from '@root/types';

interface SubscriptionSummaryProps {
  teamName: string;
  billingCycle: BillingCycle;
  onBillingCycleChange: (cycle: BillingCycle) => void;
  pricePerMember: number;
  savePercentage?: number;
  subtotal: number;
  tax: number;
  total: number;
  onBack?: () => void;
}

export const SubscriptionSummary: React.FC<SubscriptionSummaryProps> = ({
  teamName,
  billingCycle,
  onBillingCycleChange,
  pricePerMember,
  savePercentage = 20,
  subtotal,
  tax,
  total,
  onBack,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isAnnual = billingCycle === 'year';

  return (
    <Box paddingHorizontal='l' paddingVertical='xl'>
      {/* Header with back button and logo */}
      <Box flexDirection='row' alignItems='center' marginBottom='xl'>
        {onBack && (
          <Box marginRight='m'>
            <Icon
              name='ArrowLeft'
              color='textPrimary'
              onPress={onBack}
              height={28}
              width={28}
            />
          </Box>
        )}
        <Icon name='Logo' width={106} height={32} />
      </Box>

      {/* Subscribe to Team Plan */}
      <Box marginBottom='l'>
        <Text
          variant='webBodySecondary'
          color='textSecondary'
          marginBottom='xs'>
          {t('models:teams.subscriptions.subscribeToTeamPlan')}
        </Text>
        <Text variant='heading1' color='textSecondary' marginBottom='xxs'>
          ${(total / 100).toFixed(2)}
          <Text variant='webBodySecondary' color='textSecondary'>
            {' '}
            per {isAnnual ? 'year' : 'month'}
          </Text>
        </Text>
        <Text variant='webBodySecondary' color='textSecondary'>
          ${(pricePerMember / 100).toFixed(0)} /month/member billed{' '}
          {isAnnual ? 'annually' : 'monthly'}
        </Text>
      </Box>

      {/* Team Plan section with toggle */}
      <Box
        backgroundColor='white'
        borderColor='grey02'
        borderWidth={1}
        borderRadius='xs'
        padding='m'
        marginBottom='l'>
        <Box
          flexDirection='row'
          justifyContent='space-between'
          alignItems='center'>
          <Box flex={1}>
            <Text variant='webBodySecondary' color='textSecondary'>
              {teamName}
            </Text>
          </Box>
          <Text variant='webBodySecondary' color='textSecondary'>
            ${(total / 100).toFixed(2)} / {isAnnual ? 'year' : 'month'}
          </Text>
        </Box>

        {/* Annual billing toggle */}
        <Box
          backgroundColor='grey01'
          flexDirection='row'
          justifyContent='space-between'
          alignItems='center'
          marginTop='m'
          paddingTop='m'
          paddingBottom='m'
          paddingHorizontal='m'
          borderTopWidth={1}
          borderTopColor='grey02'
          borderBottomLeftRadius='xs'
          borderBottomRightRadius='xs'
          style={{
            marginLeft: -theme.spacing.m,
            marginRight: -theme.spacing.m,
            marginBottom: -theme.spacing.m,
          }}>
          <Box flexDirection='row' alignItems='center' flex={1}>
            <ToggleSwitch
              onValueChange={(value) =>
                onBillingCycleChange(value ? 'year' : 'month')
              }
              testID='chat-notification-toggle'
              value={isAnnual}
            />

            {savePercentage && (
              <Box
                backgroundColor='greenSecondaryMild'
                borderRadius='xxs'
                marginEnd='xs'
                marginStart='xs'
                paddingHorizontal='xs'
                paddingVertical='xxxs'>
                <Text variant='metadata' color='greenSecondary'>
                  {'Save ' + savePercentage + '%'}
                </Text>
              </Box>
            )}
            <Text variant='webBodySecondary' color='grey04'>
              with annual billing
            </Text>
          </Box>
          <Text variant='webBodySecondary' color='grey04'>
            ${(pricePerMember / 100).toFixed(0)} /month/member
          </Text>
        </Box>
      </Box>

      {/* Pricing breakdown */}
      <Box marginBottom='l'>
        <Box
          flexDirection='row'
          justifyContent='space-between'
          marginBottom='s'>
          <Text variant='webBodySecondary' color='textPrimary'>
            Subtotal
          </Text>
          <Text variant='webBodySecondary' color='textPrimary'>
            ${(subtotal / 100).toFixed(2)}
          </Text>
        </Box>
        <Box
          flexDirection='row'
          justifyContent='space-between'
          marginBottom='s'>
          <Box flexDirection='row'>
            <Text variant='webBodySecondary' color='textPrimary'>
              Tax
            </Text>
            <Icon name='Info' height={16} width={16} marginStart='xxs' />
          </Box>
          <Text variant='webBodySecondary' color='grey04'>
            ${(tax / 100).toFixed(2)}
          </Text>
        </Box>
        <Box
          flexDirection='row'
          justifyContent='space-between'
          paddingTop='s'
          borderTopWidth={1}
          borderTopColor='grey02'>
          <Text variant='labelEmphasized' color='textPrimary'>
            Total due today
          </Text>
          <Text variant='labelEmphasized' color='textPrimary'>
            ${(total / 100).toFixed(2)}
          </Text>
        </Box>
      </Box>
    </Box>
  );
};
